import {
  Text,
  GreyGrey20,
  GreyGrey30,
  GreyGrey60,
  GreyGrey70,
  GreyGrey90,
  GreyWhite,
  IndigoIndigo40,
  IndigoIndigo50,
  Link,
  Primary12Regular,
  Primary13SemiBold,
  IconChevronDownSmall,
  Box,
  Flex,
  Grid,
  GridItem
} from '@sequencehq/core-components'
import { SubNavLink } from 'components/NavigationV3/components/NavLinks/SubNavLink'
import {
  NavLink as NavLinkProps,
  SubNavLink as SubNavLinkProps
} from 'components/NavigationV3/types'
import { NavTooltip } from 'components/NavigationV3/components/NavTooltip'
import { useNavExpansionState } from 'components/NavigationV3/hooks/useNavExpansionState'
import { FC, KeyboardEvent, MouseEvent } from 'react'
import Spinner from 'components/Loading'

type Props = NavLinkProps & {
  pathname: string
  isCollapsed?: boolean
}

const pathWithoutParams = (path: string): string => path.split('?')[0]

export const NavLink = (props: Props) => {
  const { enabled = true, subLinks } = props

  if (!enabled) {
    return null
  }

  if (subLinks && subLinks.length > 0) {
    return <NavLinkGroup {...props} subLinks={subLinks} />
  }

  return <NavLinkLeaf {...props} />
}

const NavLinkLeaf: FC<Props> = ({
  'data-testid': testId,
  icon: Icon,
  label,
  href,
  pathname,
  isCollapsed = false,
  count,
  isLoading
}) => {
  const isActive: boolean = pathname.startsWith(pathWithoutParams(href))

  return (
    <NavLinkButton
      data-testid={testId}
      href={href}
      icon={Icon}
      isActive={isActive}
      isCollapsed={isCollapsed}
      label={label}
      count={count}
      isLoading={isLoading}
    />
  )
}

/**
 * A menu item with sub-links. Clicking the row keeps the familiar behaviour —
 * navigate to the first child (its href), which expands the group. The caret
 * is a separate control that expands/collapses the children in place without
 * navigating, so the new collapse behaviour doesn't disturb muscle memory.
 */
const NavLinkGroup: FC<Props & { subLinks: SubNavLinkProps[] }> = ({
  'data-testid': testId,
  icon: Icon,
  label,
  href,
  pathname,
  isCollapsed = false,
  count,
  isLoading,
  subLinks
}) => {
  const matchesSubLink = (path: string): boolean =>
    subLinks.some(subLink => path.startsWith(pathWithoutParams(subLink.href)))

  const isSubLinkActive: boolean = matchesSubLink(pathname)

  const { isExpanded, toggle } = useNavExpansionState({
    storageKey: `nav-item-${pathWithoutParams(href)}`,
    defaultIsExpanded: isSubLinkActive,
    expandOnMatchingPath: matchesSubLink
  })

  const isActive: boolean =
    pathname.startsWith(pathWithoutParams(href)) || isSubLinkActive

  // The caret toggles expansion instead of following the row's navigation.
  const handleCaretToggle = (event: MouseEvent | KeyboardEvent) => {
    event.preventDefault()
    event.stopPropagation()
    toggle()
  }

  return (
    <>
      <NavLinkButton
        data-testid={testId}
        href={href}
        icon={Icon}
        isActive={isActive}
        isExpanded={isExpanded}
        isCollapsed={isCollapsed}
        label={label}
        count={count}
        isLoading={isLoading}
        onCaretToggle={handleCaretToggle}
      />

      {isExpanded && (
        <Flex direction="column">
          {subLinks.map((subLink, index) => (
            <SubNavLink
              key={subLink.href}
              {...subLink}
              index={index}
              subLinks={subLinks}
              pathname={pathname}
              isCollapsed={isCollapsed}
            />
          ))}
        </Flex>
      )}
    </>
  )
}

export const NavLinkButton: FC<{
  'data-testid'?: string
  href: string
  icon: NavLinkProps['icon']
  isActive: boolean
  isExpanded?: boolean // if not specified, we don't show a chevron
  isCollapsed?: boolean
  label: string
  count?: number
  isLoading?: boolean
  // Toggles expansion when the caret is activated; the row itself still navigates
  onCaretToggle?: (event: MouseEvent | KeyboardEvent) => void
}> = ({
  'data-testid': testId,
  href,
  isActive,
  isExpanded,
  icon: Icon,
  label,
  isCollapsed = false,
  count,
  isLoading = false,
  onCaretToggle
}) => {
  const showCount = typeof count === 'number' && count > 0

  const navLink = (
    <Grid
      data-testid={testId}
      as={Link.Unstyled}
      to={href}
      padding="0 8px"
      templateColumns={isCollapsed ? 'auto' : 'auto 1fr'}
      height="32px"
      aria-label=""
      borderRadius="6px"
      alignContent="center"
      alignItems="center"
      justifyContent={'flex-start'}
      cursor="pointer"
      color={isActive ? GreyGrey90 : GreyGrey70}
      backgroundColor={
        isActive
          ? `color-mix(in srgb, ${GreyGrey30} 40%, ${GreyGrey20})`
          : 'transparent'
      }
      width={isCollapsed ? '32px' : 'auto'}
      style={{
        willChange: 'width, transform',
        transform: 'translateZ(0)',
        transition: 'background-color 0.2s ease'
      }}
      _hover={{
        backgroundColor: `color-mix(in srgb, ${GreyGrey30} 40%, ${GreyGrey20})`,
        color: GreyGrey90
      }}
      _focusVisible={{
        outline: 'none',
        boxShadow: `0 0 0 2px ${IndigoIndigo40} `,
        _disabled: {
          boxShadow: 'none'
        }
      }}
    >
      <GridItem
        height="fit-content"
        display="flex"
        alignContent="center"
        justifyContent={'flex-start'}
        flexShrink={0}
        alignSelf="center"
        style={{
          willChange: 'transform',
          transform: 'translateZ(0)',
          transition: 'transform 0.3s ease'
        }}
        marginRight="8px"
      >
        {isLoading ? (
          <Spinner width="16px" height="16px" color="inherit" />
        ) : (
          <Icon color="inherit" />
        )}
        {isCollapsed && showCount && (
          <Box
            width="10px"
            height="10px"
            position="absolute"
            top="-8px"
            right="-8px"
            background={IndigoIndigo50}
            border={`${GreyWhite} 1px solid`}
            borderRadius="100%"
          />
        )}
      </GridItem>
      {!isCollapsed && (
        <GridItem
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          overflow="hidden"
          className="nav-text"
        >
          {/* Label + caret sit together on the left, 4px apart. */}
          <Flex alignItems="center" gap="4px" minWidth="0">
            <Text
              {...Primary13SemiBold}
              color="inherit"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {label}
            </Text>
            {isExpanded !== undefined && (
              <Box
                as="span"
                role="button"
                tabIndex={0}
                aria-label={`Toggle ${label} links`}
                aria-expanded={isExpanded}
                data-testid={testId ? `${testId}.caret` : undefined}
                onClick={onCaretToggle}
                onKeyDown={(event: KeyboardEvent) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    onCaretToggle?.(event)
                  }
                }}
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                cursor="pointer"
                color={GreyGrey60}
                _hover={{ color: GreyGrey90 }}
                padding="2px"
                margin="-2px"
              >
                <IconChevronDownSmall
                  color="inherit"
                  style={{
                    transition: 'transform 0.2s ease',
                    transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'
                  }}
                />
              </Box>
            )}
          </Flex>

          {showCount && (
            <Flex
              data-testid={testId ? `${testId}.count` : undefined}
              backgroundColor={IndigoIndigo50}
              borderRadius="25px"
              height="18px"
              minWidth="18px"
              alignItems="center"
              justifyContent="center"
              padding="0 6px"
              color={GreyWhite}
              {...Primary12Regular}
            >
              {count}
            </Flex>
          )}
        </GridItem>
      )}
    </Grid>
  )

  if (isCollapsed) {
    return (
      <NavTooltip label={label} isDisabled={!isCollapsed}>
        {navLink}
      </NavTooltip>
    )
  }

  return navLink
}
